import { Lock, Pencil, Trash2 } from "lucide-react";
import { Checkbox } from "../../ui/checkbox";
import { fmtDate, STATUSES } from "./format";

const StatusBox = ({ c, k, label, onToggle, canEdit, tid = "crm" }) => (
  <Checkbox
    checked={!!c[k]}
    disabled={!canEdit(c)}
    onCheckedChange={(v) => onToggle(c, k, v === true)}
    aria-label={label}
    data-testid={`${tid}-toggle-${k}-${c.id}`}
    className="h-5 w-5 rounded-md border-white/25 data-[state=checked]:border-gold"
  />
);

const RowActions = ({ c, onEdit, onDelete, canEdit, tid = "crm" }) => canEdit(c) ? (
  <div className="flex items-center gap-1">
    <button onClick={() => onEdit(c)} data-testid={`${tid}-edit-${c.id}`} aria-label="Edit" className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-white/5 hover:text-gold">
      <Pencil className="h-4 w-4" />
    </button>
    <button onClick={() => onDelete(c)} data-testid={`${tid}-delete-${c.id}`} aria-label="Delete" className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-red-500/10 hover:text-red-400">
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
) : (
  <span data-testid={`${tid}-readonly-${c.id}`} title="Only the owner or the responsible admin can edit" className="flex h-9 items-center gap-1.5 px-2 text-[12px] text-zinc-400">
    <Lock className="h-3.5 w-3.5" /> View only
  </span>
);

const Audit = ({ c }) => (
  <p className="mt-0.5 text-[12px] text-zinc-400">
    by {c.created_by_name}{c.updated_by !== c.created_by || c.updated_at !== c.created_at ? ` · edited by ${c.updated_by_name}` : ""}
  </p>
);

const DesktopTable = ({ clients, ...h }) => (
  <div className="hidden overflow-x-auto rounded-[24px] border border-white/[0.07] bg-surface/70 md:block" data-testid="crm-table">
    <table className="w-full min-w-[900px] text-sm">
      <thead>
        <tr className="border-b border-white/5 text-left font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
          {["Admin", "Client name", "Date", "Time", ...STATUSES.map((s) => s.label), "Notes", ""].map((h2, i) => (
            <th key={i} className={`px-4 py-4 font-normal ${i >= 4 && i <= 6 ? "text-center" : ""}`}>{h2}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {clients.map((c) => (
          <tr key={c.id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]" data-testid={`crm-row-${c.id}`}>
            <td className="px-4 py-3"><span className="text-zinc-100">{c.admin_name}</span><Audit c={c} /></td>
            <td className="px-4 py-3 font-medium text-zinc-50" data-testid={`crm-name-${c.id}`}>{c.client_name}</td>
            <td className="px-4 py-3 font-mono text-zinc-300">{fmtDate(c.date)}</td>
            <td className="px-4 py-3 font-mono text-zinc-300">{c.time}</td>
            {STATUSES.map((s) => (
              <td key={s.key} className="px-4 py-3 text-center"><StatusBox c={c} k={s.key} label={s.label} onToggle={h.onToggle} canEdit={h.canEdit} /></td>
            ))}
            <td className="max-w-[220px] px-4 py-3 text-zinc-300"><p className="truncate" title={c.notes}>{c.notes || "—"}</p></td>
            <td className="px-2 py-3"><RowActions c={c} {...h} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MobileCards = ({ clients, ...h }) => (
  <div className="space-y-3 md:hidden" data-testid="crm-cards">
    {clients.map((c) => (
      <div key={c.id} className="rounded-[24px] border border-white/[0.07] bg-surface/70 p-4" data-testid={`crm-card-${c.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold text-zinc-50">{c.client_name}</p>
            <p className="mt-0.5 font-mono text-xs text-zinc-400">{fmtDate(c.date)} · {c.time} · {c.admin_name}</p>
          </div>
          <RowActions c={c} {...h} tid="crm-m" />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {STATUSES.map((s) => (
            <label key={s.key} className="flex flex-col items-center gap-2 rounded-2xl border border-white/5 bg-white/[0.03] px-2 py-3 text-center text-[12px] text-zinc-300">
              <StatusBox c={c} k={s.key} label={s.label} onToggle={h.onToggle} canEdit={h.canEdit} tid="crm-m" />
              {s.label}
            </label>
          ))}
        </div>
        {c.notes && <p className="mt-3 text-sm text-zinc-300">{c.notes}</p>}
        <Audit c={c} />
      </div>
    ))}
  </div>
);

export const ClientList = ({ clients, loading, ...handlers }) => {
  if (!clients.length) {
    return (
      <div className="rounded-[24px] border border-dashed border-white/10 py-16 text-center text-sm text-zinc-400" data-testid="crm-empty">
        {loading ? "Loading clients…" : "No clients yet. Use “Add client” to create the first entry."}
      </div>
    );
  }
  return (
    <>
      <DesktopTable clients={clients} {...handlers} />
      <MobileCards clients={clients} {...handlers} />
    </>
  );
};
